import Vue from 'vue'
import Router, { RouteConfig } from 'vue-router'
import store from '@/store/index'
import qs from 'qs'
import { default as autoRoutes } from 'vue-auto-routing'
import { setModalComponent, handleIntended } from '@/utils/helper'
import { createRouterLayout } from 'vue-router-layout'
import { setSearchStateByQuery } from '@/store/modules/search/from-query'
import { pushDataLayer } from './tools/gtm'

Vue.use(Router)

const RouterLayout = createRouterLayout((layout) => {
  return import('@/layouts/' + layout + '.vue')
})

const customRoutes: RouteConfig[] = [
  {
    path: '_search', // TODO: 消す
    beforeEnter: (to, from, next) => {
      setSearchStateByQuery(to.query)
      setModalComponent(
        to,
        from,
        () =>
          import('@/components/modal/SearchProgramModal/SearchProgramModal.vue')
      )
      next()
    },
    meta: { hideFooter: true },
  },
  {
    path: '/me/notifications',
    beforeEnter: (to, from, next) => {
      setModalComponent(
        to,
        from,
        () =>
          import(
            '@/components/profile/modal/EmailNotificationSettingsModal.vue'
          )
      )
      next()
    },
    meta: { hideFooter: true },
  },
]

const router = new Router({
  mode: 'history',
  base: '/',
  scrollBehavior(to, from, savedPosition) {
    if (from && to) {
      if (to.fullPath.split('?')[0] == from.fullPath.split('?')[0])
        return savedPosition
    }

    return { x: 0, y: 0 }
  },
  parseQuery: (query) => {
    return qs.parse(query)
  },
  stringifyQuery(query) {
    const result = qs.stringify(query)
    return result ? '?' + result : ''
  },
  routes: [
    {
      path: '/',
      component: RouterLayout,
      children: [
        ...autoRoutes,
        ...customRoutes,
        // indexページのないrouteは下層にリダイレクトする
        { path: '/help', redirect: '/' },
        { path: '/profiles', redirect: '/' },
        { path: '/reservations', redirect: '/' },
        { path: '/rankings', redirect: '/' },
        { path: '/corp', redirect: '/' },
        // 404
        {
          path: '*',
          component: () =>
            import('@/components/globals/ErrorPages/NotFoundPage.vue'),
        },
      ],
    },
  ],
})

const needEmailVerifiedRoutes = [
  '/me',
  '/me/reservations',
  '/me/profile',
  '/me/points',
  '/me/favorites',
  '/me/subscription/cancel',
]

/**
 * Next.js側(gyym-next)に渡すときのrouteはフルリロードする
 * VUE_APP_DISABLE_REACT_PROXY_MODE=trueで無効化できる
 */
const proxiedPaths = [
  '/',
  '/about',
  '/me',
  '/me/referral',
  '/me/reservations',
  '/me/points',
  '/me/points/cards',
  '/me/points/receipt',
  '/search',
  '/signup',
  '/login',
]

router.beforeEach((to, from, next) => {
  if (
    !process.env.VUE_APP_DISABLE_REACT_PROXY_MODE ||
    process.env.VUE_APP_DISABLE_REACT_PROXY_MODE === 'false'
  ) {
    const reload = () => {
      next(false)
      const q = qs.stringify(to.query, { addQueryPrefix: true })
      const href = `${window.location.protocol}//${window.location.host}${to.path}${q}`
      window.location.href = href
    }

    if (proxiedPaths.includes(to.path)) {
      return reload()
    }

    if (to.path.startsWith('/facilities')) {
      const segments = to.path.split('/')

      // Reload everything except reservation requests (which is still inside vue app)
      if (segments[7] !== 'reservation_request') {
        return reload()
      }
    }
  }

  // programsページのクエリをstoreにセットする
  if (to.path === '/programs') {
    setSearchStateByQuery(to.query)
  }

  // trailing slash
  if (to.path !== '/' && to.path.match(/\/+$/)) {
    return next({ ...to, path: to.path.replace(/\/+$/, '') })
  }

  if (
    needEmailVerifiedRoutes.includes(to.path) &&
    (!store.getters['auth/isLogin'] ||
      (!store.getters['auth/emailVerified'] && store.getters['auth/isLogin']))
  ) {
    if (router.app.$modal) {
      return router.app.$modal.push('EmailVerifyModal')
    }
    return next(`/login?path=${encodeURIComponent(to.fullPath)}`)
  }

  const guestOnlyRoutes = ['/login', '/signup']
  const accessGuestRoutesWithLogin =
    store.getters['auth/isLogin'] && guestOnlyRoutes.includes(to.path)
  if (accessGuestRoutesWithLogin) {
    return next('/')
  }

  // 次に飛びたいページをintendedとして保存しておく。intendedに来たらintendedを消去する
  if (!to.query.intended && from.query.intended) {
    return next(handleIntended(to, from))
  }
  next()
})

router.afterEach((to) => {
  // gtm
  const gtmParam: any = {}
  if (store.getters['auth/user'].id) {
    gtmParam.user_id = store.getters['auth/user'].id
    gtmParam.sex = store.getters['auth/user'].sex
    gtmParam.age = store.getters['auth/user'].age
    gtmParam.prefecture = store.getters['auth/user'].prefecture
    gtmParam.postal_code = store.getters['auth/user'].postalCode
  }
  if (to.params.programId) {
    gtmParam.program_id = Number(to.params.programId)
  }
  if (to.params.facilityId) {
    gtmParam.facility_id = Number(to.params.facilityId)
  }
  pushDataLayer(gtmParam)
})

export default router
